import { API, apiFetch } from "../shared/api"
import type { ApiSuccessResponse, ApiErrorResponse } from "../shared/responses"

/**
 * 「気になる」系 API 固有: 未ログイン時のステータス。
 * トグルではクリック時に /login へ誘導、状態取得では未キープ扱いにする。
 */
export type FavoriteNotLoggedInResponse = { status: "not_logged_in" }

/**
 * 「気になる」系 API の非成功レスポンス(未ログイン or 共通エラー)。
 * トグル/状態取得の双方で失敗アームは共通。
 */
type FavoriteApiFailure = FavoriteNotLoggedInResponse | ApiErrorResponse

/**
 * 「気になる」(お気に入り求人) API のレスポンス。
 */
export type FavoriteOfferResponse = { status: "success" } | FavoriteApiFailure

/**
 * 「気になる」(お気に入り求人) API のクライアント。
 *
 * エンドポイント: POST /api/v1/favorite_offers/:offer_id/(favorite|unfavorite)
 * - Api::V1::BaseController (ActionController::API) 配下なので CSRF 検証はされないが、
 *   共通 fetch ラッパー(apiFetch)経由で他 API と同じヘッダーを送る
 * - 認証はセッション cookie (sorcery current_user)。同一オリジンなら自動送信される
 */
export const favoriteOffer = (
    offerId: string
): Promise<FavoriteOfferResponse> =>
    apiFetch<FavoriteOfferResponse>(API.ENDPOINTS.FAVORITE_OFFER(offerId), {
        method: "POST",
    })

export const unfavoriteOffer = (
    offerId: string
): Promise<FavoriteOfferResponse> =>
    apiFetch<FavoriteOfferResponse>(API.ENDPOINTS.UNFAVORITE_OFFER(offerId), {
        method: "POST",
    })

/**
 * 「気になる」状態取得 API のレスポンス。
 * - 成功時は共通の ApiSuccessResponse。data.favorite_offer_ids に、渡した offer_ids のうち
 *   キープ済みの id(数値)配列が返る
 * - 失敗アーム(not_logged_in / error)はトグルと共通の FavoriteApiFailure
 */
export type FavoriteOffersStatusResponse =
    | ApiSuccessResponse<{ favorite_offer_ids: number[] }>
    | FavoriteApiFailure

/**
 * 「気になる」状態取得 API のクライアント。
 *
 * エンドポイント: GET /api/v1/favorite_offers?offer_ids[]=1&offer_ids[]=2
 * 渡した offer_ids のうち current_user がキープ済みの id を返す。
 */
export const getFavoriteOffers = (
    offerIds: string[]
): Promise<FavoriteOffersStatusResponse> =>
    apiFetch<FavoriteOffersStatusResponse>(
        API.ENDPOINTS.FAVORITE_OFFERS(offerIds),
        { method: "GET" }
    )
